/**
 * Questionnaire widgets + Runner partagés entre toutes les tranches d'âge.
 * Chaque questionnaire (45-50, 18-25, 60-65, 70-75) exporte un `buildXXXXSteps()`
 * qui retourne un tableau d'étapes, passé à ce runner générique.
 */
import { useEffect, useRef, useState } from 'react';
import { ChevronLeft, ChevronRight, Check } from 'lucide-react';
import { MultiCheck, SECTION_EMOJI } from './BilanSteps';

export { SECTION_EMOJI, MultiCheck };

// Light haptic feedback on tap (iOS 16.4+ and Android)
export function hapticTap() {
  try {
    if (typeof navigator !== 'undefined' && typeof navigator.vibrate === 'function') {
      navigator.vibrate(10);
    }
  } catch { /* ignore */ }
}

// ─── Chapter definitions — used by the runner to group questions ─────────────
export const CHAPTERS = [
  { id: 'you', label: 'Vous', emoji: '👤', match: (id) => id === 'sex' },
  { id: 'atcd', label: 'Parcours', emoji: '📋', match: (id) => /^(motifs|atcd_perso|atcd_fam|traitements|allergies)$/.test(id) },
  { id: 'habits', label: 'Habitudes', emoji: '💪', match: (id) => /^(tabac|alcool_|activite|ali_|sommeil|ecrans|stress|cannabis|sex_act|contra|ist_depist|grossesse|eau|sel)/.test(id) },
  { id: 'mental', label: 'Bien-être', emoji: '🧠', match: (id) => /^(phq2_|gad2_|peur_chuter|aides_domicile)/.test(id) },
  { id: 'screening', label: 'Santé', emoji: '🩺', match: (id) => /^(vaccin_|depistage_|cv_|menopause|adl|iadl|chutes)/.test(id) },
  { id: 'signals', label: 'Signaux', emoji: '🚨', match: (id) => id === 'redflags' },
];

export function getChapterForStep(stepId) {
  for (const c of CHAPTERS) {
    if (c.match(stepId)) return c;
  }
  return CHAPTERS[0];
}

export function Choice({ options, value, onChange, testid }) {
  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mt-3" data-testid={testid}>
      {options.map((o) => (
        <button type="button" key={o.value}
          onClick={() => { hapticTap(); onChange(o.value); }}
          className={`py-3 px-2 rounded-2xl text-sm font-medium transition-all border-2 active:scale-95 ${value === o.value ? 'bg-teal-500 border-teal-500 text-white shadow-md' : 'bg-white border-slate-200 text-slate-700 hover:border-teal-300'}`}>
          {o.label}
        </button>
      ))}
    </div>
  );
}

/**
 * Slider — optionnellement accompagné de presets (chips cliquables sous le slider)
 * et de zones qualitatives (bandes colorées indicatives).
 *
 * Props :
 *   presets = [{ value: 0, label: 'Jamais' }, ...]
 *   zones   = [{ from: 0, to: 75, color: 'rose', label: 'Insuffisant' }, ...]
 */
export function Slider({ value, onChange, min, max, step = 1, unit = '', testid, presets, zones }) {
  return (
    <div className="mt-4">
      <div className="text-center text-3xl font-bold text-teal-600 mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
        {value == null ? '—' : value}<span className="text-base font-normal text-slate-400 ml-1">{unit}</span>
      </div>
      <input type="range" min={min} max={max} step={step} value={value ?? min}
        onChange={(e) => onChange(Number(e.target.value))}
        data-testid={testid}
        className="w-full h-3 bg-slate-200 rounded-full accent-teal-500 cursor-pointer" />
      <div className="flex justify-between text-xs text-slate-400 mt-1"><span>{min}{unit}</span><span>{max}{unit}</span></div>
      {Array.isArray(zones) && zones.length > 0 && (
        <div className="flex gap-1 mt-2" aria-hidden="true">
          {zones.map((z, i) => (
            <div key={i} className={`flex-1 h-2 rounded-full ${z.color}`} title={z.label} />
          ))}
        </div>
      )}
      {Array.isArray(zones) && zones.length > 0 && (
        <div className="flex justify-between text-[10px] text-slate-400 mt-1">
          {zones.map((z, i) => <span key={i}>{z.label}</span>)}
        </div>
      )}
      {Array.isArray(presets) && presets.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mt-3 justify-center">
          {presets.map((p) => {
            const active = value === p.value;
            return (
              <button type="button" key={p.value}
                onClick={() => { hapticTap(); onChange(p.value); }}
                className={`px-3 py-1.5 rounded-full text-[12px] font-medium border transition-all active:scale-95 ${active ? 'bg-teal-500 border-teal-500 text-white shadow-sm' : 'bg-white border-slate-200 text-slate-600 hover:border-teal-300'}`}>
                {p.label}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

export function Counter({ value, onChange, min = 0, max = 99, unit = '', testid }) {
  const v = Number.isFinite(value) ? value : 0;
  return (
    <div className="flex items-center justify-center gap-4 mt-4" data-testid={testid}>
      <button type="button"
        onClick={() => { hapticTap(); onChange(Math.max(min, v - 1)); }}
        aria-label="Diminuer"
        className="w-14 h-14 rounded-full bg-slate-100 hover:bg-slate-200 text-2xl font-bold text-slate-600 select-none">−</button>
      <div className="w-28 text-center text-3xl font-bold text-teal-600 select-none tabular-nums" style={{ fontFamily: 'Outfit, sans-serif' }}>
        {v}<span className="text-sm font-normal text-slate-400 ml-1">{unit}</span>
      </div>
      <button type="button"
        onClick={() => { hapticTap(); onChange(Math.min(max, v + 1)); }}
        aria-label="Augmenter"
        className="w-14 h-14 rounded-full bg-slate-100 hover:bg-slate-200 text-2xl font-bold text-slate-600 select-none">+</button>
    </div>
  );
}

// Yes/No/NSP shortcut (used many times across questionnaires)
export const YES_NO_NSP = [
  { value: 'ok', label: 'À jour' },
  { value: 'nsp', label: 'Pas sûr(e)' },
  { value: 'no', label: 'Non' },
];
export const YES_NO_NSP_BIN = [
  { value: 'yes', label: 'Oui' },
  { value: 'no', label: 'Non' },
  { value: 'nsp', label: 'Je ne sais pas' },
];

// PHQ-2 / GAD-2 answer scale (shared)
export const PHQ_GAD_OPTIONS = [
  { value: 0, label: 'Jamais' },
  { value: 1, label: 'Plusieurs jours' },
  { value: 2, label: 'Plus de la moitié du temps' },
  { value: 3, label: 'Presque tous les jours' },
];

// Extra helper: set a nested key inside answers.extra_responses
export function updateExtra(answers, update, key, value) {
  update({ extra_responses: { ...(answers.extra_responses || {}), [key]: value } });
}
export function getExtra(answers, key, fallback = undefined) {
  return (answers.extra_responses || {})[key] ?? fallback;
}

// ---- Questionnaire Runner (generic) --------------------------------------
const MILESTONES = [
  { threshold: 25, text: '👍 C\'est parti, super départ !' },
  { threshold: 50, text: '💪 Vous êtes à la moitié !' },
  { threshold: 75, text: '🏁 Plus que quelques questions...' },
];

export function QuestionnaireRunner({ buildSteps, config, answers, update, onDone, onBack, initialIdx = 0, onIdxChange }) {
  const [idx, setIdxInternal] = useState(initialIdx);
  const [milestone, setMilestone] = useState(null);
  const lastChapterRef = useRef(null);
  const [chapterBannerVisible, setChapterBannerVisible] = useState(false);
  const shownMilestonesRef = useRef(new Set());

  const setIdx = (updater) => {
    setIdxInternal((prev) => {
      const next = typeof updater === 'function' ? updater(prev) : updater;
      onIdxChange?.(next);
      return next;
    });
  };
  const steps = buildSteps(config, answers, update);
  const safeIdx = Math.min(idx, steps.length - 1);
  const step = steps[safeIdx];
  const progress = ((safeIdx + 1) / steps.length) * 100;
  // Auto-advance — called by single-choice steps after the user picks an option
  const advance = () => {
    setTimeout(() => setIdx((i) => Math.min(i + 1, steps.length - 1)), 250);
  };

  // ─── Chapter detection (unique chapters visible in this questionnaire) ─────
  const currentChapter = step ? getChapterForStep(step.id) : CHAPTERS[0];
  const visibleChapters = [];
  for (const s of steps) {
    const ch = getChapterForStep(s.id);
    if (!visibleChapters.find((c) => c.id === ch.id)) visibleChapters.push(ch);
  }
  const currentChapterIdx = visibleChapters.findIndex((c) => c.id === currentChapter.id);

  // ─── Chapter banner slide-in when changing chapter ─────────────────────────
  useEffect(() => {
    if (!step) return;
    if (lastChapterRef.current !== currentChapter.id) {
      lastChapterRef.current = currentChapter.id;
      setChapterBannerVisible(true);
      const t = setTimeout(() => setChapterBannerVisible(false), 2400);
      return () => clearTimeout(t);
    }
  }, [currentChapter.id, step]);

  // ─── Milestone toast (25% / 50% / 75%) ─────────────────────────────────────
  useEffect(() => {
    for (const m of MILESTONES) {
      if (progress >= m.threshold && !shownMilestonesRef.current.has(m.threshold)) {
        shownMilestonesRef.current.add(m.threshold);
        setMilestone(m.text);
        const t = setTimeout(() => setMilestone(null), 2000);
        return () => clearTimeout(t);
      }
    }
  }, [progress]);

  if (!step) return null;
  const isAutoAdvance = step.autoAdvance || step.render.length >= 3;

  return (
    <div className="max-w-lg mx-auto pt-4 px-4 pb-24" data-testid={`q-step-${step.id}`}>
      {/* ─── Chapter progress (segmented) ─── */}
      <div className="mb-3" data-testid="q-chapter-bar">
        <div className="flex gap-1.5 mb-2">
          {visibleChapters.map((c, i) => {
            const isActive = i === currentChapterIdx;
            const isDone = i < currentChapterIdx;
            return (
              <div key={c.id} className="flex-1 h-1.5 rounded-full overflow-hidden bg-slate-100">
                <div className={`h-full transition-all duration-500 ${isDone ? 'bg-teal-400' : isActive ? 'bg-gradient-to-r from-teal-400 to-sky-500' : 'bg-transparent'}`}
                  style={{ width: isDone ? '100%' : isActive ? `${((safeIdx - steps.findIndex((s) => getChapterForStep(s.id).id === c.id) + 1) / steps.filter((s) => getChapterForStep(s.id).id === c.id).length) * 100}%` : '0%' }} />
              </div>
            );
          })}
        </div>
        <div className="flex items-center justify-between text-[11px]">
          <span className="text-slate-500 font-medium inline-flex items-center gap-1">
            <span>{currentChapter.emoji}</span>
            <span>{currentChapter.label}</span>
            <span className="text-slate-300">·</span>
            <span className="text-slate-400">Chapitre {currentChapterIdx + 1} / {visibleChapters.length}</span>
          </span>
          <span className="text-slate-400">{Math.round(progress)} %</span>
        </div>
      </div>

      {/* ─── Chapter slide-in banner ─── */}
      <div className={`overflow-hidden transition-all duration-300 ${chapterBannerVisible ? 'max-h-24 opacity-100 mb-3' : 'max-h-0 opacity-0 mb-0'}`}
        data-testid="q-chapter-banner" aria-live="polite">
        <div className="p-3 rounded-2xl bg-gradient-to-r from-teal-50 to-sky-50 border border-teal-100 flex items-center gap-3">
          <div className="text-2xl">{currentChapter.emoji}</div>
          <div className="flex-1 min-w-0">
            <p className="text-[11px] uppercase tracking-wider text-teal-600 font-semibold">Nouveau chapitre</p>
            <p className="text-sm font-bold text-slate-800" style={{ fontFamily: 'Outfit, sans-serif' }}>{currentChapter.label}</p>
          </div>
        </div>
      </div>

      {/* ─── Milestone toast ─── */}
      {milestone && (
        <div className="fixed top-4 left-1/2 -translate-x-1/2 z-50 pointer-events-none"
          data-testid="q-milestone-toast" role="status">
          <div className="px-4 py-2 bg-white rounded-full shadow-lg border border-teal-200 text-sm font-medium text-slate-700 animate-bounce">
            {milestone}
          </div>
        </div>
      )}

      {/* ─── Question content with fade-in animation ─── */}
      <div key={`${step.id}-${safeIdx}`} className="animate-step-fade">
        <div className="flex items-start gap-3 mt-4 mb-3">
          <div className="text-3xl">{step.emoji || ''}</div>
          <div className="flex-1">
            <h2 className="text-lg font-bold text-slate-800 leading-snug" style={{ fontFamily: 'Outfit, sans-serif' }}>{step.title}</h2>
            {step.subtitle && <p className="text-xs text-slate-500 mt-1">{step.subtitle}</p>}
          </div>
        </div>

        <div>{step.render(answers, update, advance)}</div>
      </div>

      {/* ─── Nav buttons ─── */}
      <div className="mt-8 flex gap-3 justify-center">
        <button type="button"
          onClick={() => { hapticTap(); (safeIdx === 0 ? onBack() : setIdx((i) => i - 1)); }}
          data-testid="q-prev"
          className={`py-3 border-2 border-slate-200 text-slate-500 rounded-full font-medium hover:bg-slate-50 inline-flex items-center justify-center gap-1 ${isAutoAdvance ? 'px-8' : 'flex-1'}`}>
          <ChevronLeft className="w-4 h-4" /> Retour
        </button>
        {!isAutoAdvance && (
          safeIdx < steps.length - 1 ? (
            <button type="button"
              onClick={() => { hapticTap(); setIdx((i) => i + 1); }}
              data-testid="q-next"
              className="flex-1 py-3 bg-gradient-to-r from-teal-500 to-sky-500 text-white rounded-full font-semibold shadow-md inline-flex items-center justify-center gap-1 active:scale-95 transition-transform">
              Suivant <ChevronRight className="w-4 h-4" />
            </button>
          ) : (
            <button type="button"
              onClick={() => { hapticTap(); onDone(); }}
              data-testid="q-submit"
              className="flex-1 py-3 bg-gradient-to-r from-emerald-500 to-teal-600 text-white rounded-full font-semibold shadow-md inline-flex items-center justify-center gap-1 active:scale-95 transition-transform">
              Terminer <Check className="w-4 h-4" />
            </button>
          )
        )}
        {isAutoAdvance && safeIdx === steps.length - 1 && (
          <button type="button"
            onClick={() => { hapticTap(); onDone(); }}
            data-testid="q-submit"
            className="flex-1 py-3 bg-gradient-to-r from-emerald-500 to-teal-600 text-white rounded-full font-semibold shadow-md inline-flex items-center justify-center gap-1 active:scale-95 transition-transform">
            Terminer <Check className="w-4 h-4" />
          </button>
        )}
      </div>

      <style>{`
        @keyframes stepFadeIn {
          from { opacity: 0; transform: translateY(8px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-step-fade {
          animation: stepFadeIn 180ms ease-out;
        }
      `}</style>
    </div>
  );
}

// Common blocks reused across questionnaires --------------------------------
export function buildCommonStartSteps(config, answers, update) {
  return [
    { id: 'sex', emoji: SECTION_EMOJI.sex, title: 'Vous êtes', render: (a, u, advance) => (
      <Choice testid="q-sex" value={answers.sex}
        onChange={(v) => { update({ sex: v }); advance(); }}
        options={[{value:'F',label:'Une femme'},{value:'H',label:'Un homme'},{value:'NR',label:'Préfère ne pas dire'}]} />
    )},
    { id: 'motifs', emoji: SECTION_EMOJI.motif, title: "Pourquoi consultez-vous aujourd'hui ?", subtitle: "Plusieurs réponses possibles",
      render: () => (
        <MultiCheck testidPrefix="q-motif" options={config.motifs || []} value={answers.motifs || []}
          onToggle={(o) => {
            const cur = answers.motifs || [];
            update({ motifs: cur.includes(o) ? cur.filter(x => x !== o) : [...cur, o] });
          }} />
    )},
    { id: 'atcd_perso', emoji: SECTION_EMOJI.atcd, title: 'Avez-vous une des maladies suivantes ?', subtitle: 'Sélectionnez celles qui vous concernent',
      render: () => (
        <MultiCheck testidPrefix="q-atcdp" options={config.atcd_personnels || []} value={answers.atcd_personnels || []}
          onToggle={(o) => {
            const cur = answers.atcd_personnels || [];
            update({ atcd_personnels: cur.includes(o) ? cur.filter(x => x !== o) : [...cur, o] });
          }} />
    )},
    { id: 'atcd_fam', emoji: SECTION_EMOJI.familial, title: 'Dans votre famille proche (parents, frères, sœurs) ?',
      render: () => (
        <MultiCheck testidPrefix="q-atcdf" options={config.atcd_familiaux || []} value={answers.atcd_familiaux || []}
          onToggle={(o) => {
            const cur = answers.atcd_familiaux || [];
            update({ atcd_familiaux: cur.includes(o) ? cur.filter(x => x !== o) : [...cur, o] });
          }} />
    )},
    { id: 'traitements', emoji: SECTION_EMOJI.traitement, title: 'Prenez-vous des médicaments ?',
      render: () => (
        <MultiCheck testidPrefix="q-trt" options={config.classes_therapeutiques || []} value={answers.traitements || []}
          onToggle={(o) => {
            const cur = answers.traitements || [];
            update({ traitements: cur.includes(o) ? cur.filter(x => x !== o) : [...cur, o] });
          }} />
    )},
    { id: 'allergies', emoji: SECTION_EMOJI.allergie, title: 'Allergies connues ?',
      render: () => (
        <MultiCheck testidPrefix="q-alg" options={config.allergies || []} value={answers.allergies || []}
          onToggle={(o) => {
            const cur = answers.allergies || [];
            update({ allergies: cur.includes(o) ? cur.filter(x => x !== o) : [...cur, o] });
          }} />
    )},
  ];
}

// Alcool AUDIT-C common block (with skip if q1 === 0)
export function buildAlcoolSteps(answers, update) {
  return [
    { id: 'alcool_q1', emoji: SECTION_EMOJI.alcool, title: "À quelle fréquence consommez-vous de l'alcool ?",
      render: (a, u, advance) => (
        <Choice testid="q-alc-q1" value={answers.alcool_audit_q1}
          onChange={(v) => {
            update({
              alcool_audit_q1: v,
              ...(v === 0 ? { alcool_audit_q2: null, alcool_audit_q3: null } : {}),
            });
            advance();
          }}
          options={[
            {value:0,label:'Jamais'},{value:1,label:'1×/mois ou moins'},{value:2,label:'2-4×/mois'},
            {value:3,label:'2-3×/semaine'},{value:4,label:'4+/semaine'},
          ]} />
    )},
    ...(answers.alcool_audit_q1 !== 0 && answers.alcool_audit_q1 != null ? [
      { id: 'alcool_q2', emoji: SECTION_EMOJI.alcool, title: 'Combien de verres les jours où vous buvez ?',
        render: (a, u, advance) => (
          <Choice testid="q-alc-q2" value={answers.alcool_audit_q2}
            onChange={(v) => { update({ alcool_audit_q2: v }); advance(); }}
            options={[{value:0,label:'1 ou 2'},{value:1,label:'3 ou 4'},{value:2,label:'5 ou 6'},{value:3,label:'7 à 9'},{value:4,label:'10+'}]} />
      )},
      { id: 'alcool_q3', emoji: SECTION_EMOJI.alcool, title: "À quelle fréquence buvez-vous 6 verres ou plus en une occasion ?",
        render: (a, u, advance) => (
          <Choice testid="q-alc-q3" value={answers.alcool_audit_q3}
            onChange={(v) => { update({ alcool_audit_q3: v }); advance(); }}
            options={[
              {value:0,label:'Jamais'},{value:1,label:'< 1×/mois'},{value:2,label:'1×/mois'},
              {value:3,label:'1×/semaine'},{value:4,label:'(presque) tous les jours'},
            ]} />
      )},
    ] : []),
  ];
}

// Tabac common block (with conditional sub-questions)
export function buildTabacSteps(answers, update) {
  return [
    { id: 'tabac', emoji: SECTION_EMOJI.tabac, title: 'Fumez-vous ou avez-vous fumé ?',
      render: () => (
        <>
          <Choice testid="q-tabac" value={answers.tabac}
            onChange={(v) => update({ tabac: v })}
            options={[{value:'never',label:'Jamais'},{value:'former',label:'Ancien fumeur'},{value:'current',label:'Fumeur actuel'}]} />
          {(answers.tabac === 'current' || answers.tabac === 'former') && (
            <div className="mt-6 p-4 bg-white rounded-2xl border-2 border-teal-100">
              <div className="text-sm text-slate-700 font-medium mb-1">Combien de cigarettes par jour ?</div>
              <Slider testid="q-tabac-cig" value={answers.tabac_cig_j} onChange={(v) => update({ tabac_cig_j: v })} min={0} max={80} step={1} unit=" cig/j"
                presets={[{ value: 5, label: '<5' }, { value: 10, label: '10' }, { value: 15, label: '15' }, { value: 20, label: '20 (1 paquet)' }, { value: 30, label: '30+' }]} />
              <div className="text-sm text-slate-700 font-medium mt-6 mb-1">Depuis combien d'années ?</div>
              <Slider testid="q-tabac-years" value={answers.tabac_years} onChange={(v) => update({ tabac_years: v })} min={0} max={70} step={1} unit=" ans"
                presets={[{ value: 5, label: '<5' }, { value: 10, label: '10' }, { value: 20, label: '20' }, { value: 30, label: '30' }, { value: 40, label: '40+' }]} />
            </div>
          )}
          {answers.tabac === 'current' && (
            <div className="mt-4">
              <div className="text-sm text-slate-700 font-medium">Souhaitez-vous arrêter ?</div>
              <Choice testid="q-tabac-stop" value={answers.tabac_wants_stop}
                onChange={(v) => update({ tabac_wants_stop: v })}
                options={[{value:'yes',label:'Oui'},{value:'no',label:'Non'},{value:'nsp',label:'Je ne sais pas'}]} />
            </div>
          )}
        </>
    )},
  ];
}

// Mental health PHQ-2 + GAD-2 common block (final wording HAS)
export function buildMentalSteps(answers, update) {
  return [
    { id: 'phq2_q1', emoji: SECTION_EMOJI.mental, title: "Au cours des 2 dernières semaines, avez-vous manqué d'intérêt ou de plaisir à faire les choses ?",
      render: (a, u, advance) => <Choice testid="q-phq-1" value={answers.phq2_q1} onChange={(v) => { update({ phq2_q1: v }); advance(); }} options={PHQ_GAD_OPTIONS} />
    },
    { id: 'phq2_q2', emoji: SECTION_EMOJI.mental, title: "Au cours des 2 dernières semaines, vous êtes-vous senti(e) triste, déprimé(e) ou sans espoir ?",
      render: (a, u, advance) => <Choice testid="q-phq-2" value={answers.phq2_q2} onChange={(v) => { update({ phq2_q2: v }); advance(); }} options={PHQ_GAD_OPTIONS} />
    },
    { id: 'gad2_q1', emoji: SECTION_EMOJI.mental, title: "Au cours des 2 dernières semaines, vous êtes-vous senti(e) nerveux(se), anxieux(se) ou à bout ?",
      render: (a, u, advance) => <Choice testid="q-gad-1" value={answers.gad2_q1} onChange={(v) => { update({ gad2_q1: v }); advance(); }} options={PHQ_GAD_OPTIONS} />
    },
    { id: 'gad2_q2', emoji: SECTION_EMOJI.mental, title: "Au cours des 2 dernières semaines, avez-vous eu du mal à vous empêcher de vous inquiéter ?",
      render: (a, u, advance) => <Choice testid="q-gad-2" value={answers.gad2_q2} onChange={(v) => { update({ gad2_q2: v }); advance(); }} options={PHQ_GAD_OPTIONS} />
    },
  ];
}

// Red flags (same 6 signals) common final step
export function buildRedFlagsStep(config, answers, update, extraFlagsLabel = null) {
  const baseFlags = config.red_flags || [];
  // Optional extras per tranche (eg. fragility-related) — label provided by caller
  return [{ id: 'redflags', emoji: SECTION_EMOJI.redflags, title: 'Signaux qui vous inquiètent ?', subtitle: "Cochez si vous avez ressenti ça récemment",
    render: () => (
      <div className="space-y-2 mt-2" data-testid="q-redflags">
        {baseFlags.map((rf) => {
          const active = !!(answers.red_flags || {})[rf.key];
          return (
            <button type="button" key={rf.key}
              onClick={() => update({ red_flags: { ...(answers.red_flags || {}), [rf.key]: !active } })}
              className={`w-full text-left p-4 rounded-2xl transition-all border-2 ${active ? 'bg-rose-500 border-rose-500 text-white shadow-md' : 'bg-white border-slate-200 text-slate-700 hover:border-rose-300'}`}>
              <div className="flex items-center justify-between gap-2">
                <span className="text-sm font-medium leading-snug">{rf.label}</span>
                {active && <Check className="w-4 h-4 flex-shrink-0" />}
              </div>
            </button>
          );
        })}
        <p className="text-xs text-slate-400 italic mt-3">Vos réponses seront transmises à votre médecin.</p>
      </div>
    )
  }];
}
